import styled from "@emotion/styled"
import { FormControl, MenuItem } from "@mui/material"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import { getYear } from "date-fns"
import { useState } from "react"
import {
  CalendarIcon,
  Container,
  Desktop,
  Mobile,
  NewText,
  TitleWithLine,
  useIsLargeDesktop,
} from "ui"
import { extractProperty } from "utils"

import FinancialYear from "./FinancialYear"

const StyledContainer = styled.div`
  padding-top: 40px;
  padding-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 60px;
    padding-bottom: 60px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 40px;
    padding-top: 80px;
    padding-bottom: 80px;
  }
`

const StyledReportsCont = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 175px 1fr;
    gap: 60px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    grid-template-columns: 262px 1fr;
    gap: 88px;
  }
`

const StyledFormControl = styled(FormControl)`
  ${(props) => props.theme.breakpoints.down("md")} {
    width: 245px;
  }
`

const StyledSelect = styled(Select)`
  fieldset {
    border: 1px solid #153e6924;
  }
  &.Mui-focused fieldset {
    border: 1px solid #153e69 !important;
  }
  :hover fieldset {
    border: 1px solid #153e69 !important;
  }
`

const StyledOutput = styled.div`
  height: 25.875px;
  display: flex;
  align-items: center;
  gap: 12px;
`
type Props = {
  data: any
  locale: string
}

function getArrayReports(data: any, propertyName: string) {
  const topArray = extractProperty(data, propertyName) || []
  const midArray = extractProperty(topArray[0], "uploadFile") || []
  return (
    midArray.map((el: any) => ({
      name: el.name,
      url: el.url,
    })) || []
  )
}
function getQuaterly(quarterly: any) {
  const quarter = quarterly?.[0]
  return {
    financialReports: getArrayReports(quarter, "financialReports"),
    investorPresentation: getArrayReports(quarter, "investorPresentation"),
    videos: getArrayReports(quarter, "videos"),
    moreVideos: getArrayReports(quarter, "moreVideos"),
    morePresentations: getArrayReports(quarter, "morePresentations"),
  }
}

function FinancialReports({ data, locale }: Props) {
  const [open, setOpen] = useState(false)
  const isLDesktop = useIsLargeDesktop()
  const years = extractProperty(data, "years")
  const yearsSelector = years.map((year: any) =>
    getYear(new Date(extractProperty(year, "yearsSelection"))),
  )
  const [selectedYearIndex, setYearIndex] = useState<string>(yearsSelector?.[0])

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value
    setYearIndex(value as string)
  }

  const allData = years.map((year: any) => {
    return {
      year: getYear(new Date(extractProperty(year, "yearsSelection"))),
      quarterly01: getQuaterly(extractProperty(year, "quarterly01")),
      quarterly02: getQuaterly(extractProperty(year, "quarterly02")),
      quarterly03: getQuaterly(extractProperty(year, "quarterly03")),
      quarterly04: getQuaterly(extractProperty(year, "quarterly04")),
    }
  })

  return (
    <Container>
      <StyledContainer>
        <TitleWithLine
          title={locale === "en" ? "Financial Reports" : "דוחות כספיים"}
          titleColor="black"
        />
        <StyledReportsCont>
          <StyledFormControl fullWidth>
            <StyledSelect
              size="small"
              sx={{
                borderRadius: "160px",
              }}
              id="select-year"
              aria-expanded={open ? "true" : undefined}
              value={selectedYearIndex}
              SelectDisplayProps={{
                "aria-label": `בחר שנה ${selectedYearIndex ? `שנה נבחרת ${selectedYearIndex}` : ""}`,
                "aria-labelledby": undefined
              }}

              onClose={() => setOpen(false)}
              onOpen={() => setOpen(true)}
              renderValue={(val) => (
                <StyledOutput>
                  <Mobile>
                    <CalendarIcon width={20} height={20} color="black" />
                  </Mobile>
                  <Desktop>
                    <CalendarIcon
                      width={isLDesktop ? 24 : 16}
                      height={isLDesktop ? 24 : 16}
                      color="black"
                    />
                  </Desktop>

                  <NewText
                    component={"span"}
                    largeDesktopFontSize={18}
                    largeDesktopLineHeight="25.2px"
                    desktopFontWeight={500}
                    desktopFontSize={12}
                    desktopLineHeight={"16.8px"}
                    mobileFontSize={16}
                    mobileLineHeight={"24px"}
                  >
                    {val as string}
                  </NewText>
                </StyledOutput>
              )}
              onChange={handleChange}
            >
              {yearsSelector.map((year: any, index: number) => (
                <MenuItem key={index.toString()} value={year} aria-describedby={"select-year"}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>
          <FinancialYear
            data={allData}
            selectedYearIndex={selectedYearIndex}
            locale={locale}
          />
        </StyledReportsCont>
      </StyledContainer>
    </Container>
  )
}

export default FinancialReports
